import { defaultLanguage } from './consts';
import { LanguageItem } from './language-item';
import { ILanguageItem, ILanguageItemContent, ILanguageResources, ILanguageResourcesCollection } from './interfaces';
import { defaultSegmentName, LanguageResourceData } from './language-item-data';

export class LanguageResources implements ILanguageResources {
  merge(languageResources: ILanguageResources) {
    if (languageResources.language !== this.language) {
      throw new Error(
        `Cannot merge language resources for different languages: ${this.language} and ${languageResources.language}`
      );
    }

    for (const entry of Array.from(languageResources.getEnumerator())) {
      const existingItem = this.get(entry.id);
      if (existingItem) {
        throw new Error(`Duplicate entry detected for ID: ${entry.id}`);
      }
      const item = this.add(entry.id, entry.getSegment() ?? '', entry.description);
      for (const segment of entry.getSegments()) {
        if (segment !== LanguageItem.DefaultSegmentName) {
          item.addSegment(segment, entry.getSegment(segment) ?? '');
        }
      }
    }
    return this;
  }

  public static readonly DefaultLanguage = defaultLanguage;

  private languageEntries: Map<string, ILanguageItem> = new Map();
  private languageEntriesByValue: Map<string, ILanguageItem> | undefined;

  private getValueMap(): Map<string, ILanguageItem> {
    if (!this.languageEntriesByValue) {
      this.languageEntriesByValue = new Map();
      for (const item of this.languageEntries.values()) {
        const segment = item.getSegment();
        this.languageEntriesByValue.set(segment?.source ?? '', item);
      }
    }
    return this.languageEntriesByValue;
  }

  public language: string = LanguageResources.DefaultLanguage;
  public name: string = '';

  add(id: string, text: string | ILanguageItemContent, description: string | null = null): ILanguageItem {
    const item = new LanguageItem(id, text, description);
    this.languageEntries.set(id.toLowerCase(), item);
    return item;
  }

  get(id: string): ILanguageItem | null {
    return this.languageEntries.get(id.toLowerCase()) || null;
  }

  getByValue(value: string): ILanguageItem | null {
    return this.getValueMap().get(value) || null;
  }

  getEnumerator(): IterableIterator<ILanguageItem> {
    return this.languageEntries.values();
  }

  public static FromLanguageItemData(data: LanguageResourceData): ILanguageResources {
    const resources = new LanguageResources();
    resources.language = data.language || LanguageResources.DefaultLanguage;
    resources.name = data.name || '';

    for (const item of data.items) {
      const langItem = resources.add(item.id, item.segments[defaultSegmentName], '');
      for (const segment of Object.keys(item.segments)) {
        if (segment !== defaultSegmentName) {
          langItem.addSegment(segment, item.segments[segment]);
        }
      }
    }

    return resources;
  }

  asLanguage(language: string): ILanguageResources {
    const result = new LanguageResources();
    result.language = language;
    result.name = this.name;
    result.languageEntries = new Map(this.languageEntries);
    return result;
  }
}

export class LanguageResourcesCollection implements ILanguageResourcesCollection {
  asLanguage(language: string) {
    const result = new LanguageResourcesCollection();
    result.resources.push(...this.resources.map(x => x.asLanguage(language)));
    return result;
  }

  readonly resources: ILanguageResources[] = [];

  public get language() {
    return this.resources.length > 0 ? this.resources[0].language : LanguageResources.DefaultLanguage;
  }

  swap(resource: ILanguageResources): void {
    const index = this.resources.findIndex(r => r.name === resource.name);
    if (index !== -1) {
      this.resources.splice(index, 1);
    }
    this.resources.push(resource);
  }

  get(id: string): ILanguageItem | null {
    for (const resource of this.resources) {
      const item = resource.get(id);
      if (item) {
        return item;
      }
    }
    return null;
  }

  getByValue(value: string): ILanguageItem | null {
    for (const resource of this.resources) {
      const item = resource.getByValue(value);
      if (item) {
        return item;
      }
    }
    return null;
  }

  getEnumerator(): IterableIterator<ILanguageItem> {
    return this.resources.flatMap(resource => Array.from(resource.getEnumerator())).values();
  }
}
